import type { DataLayerArgs } from '../types/analytics';
import { AnalyticsError } from './analyticsError';
import { analyticsLogger } from './analyticsLogger';

// Tipos para monitoreo
interface MetricData {
  timestamp: number;
  value: number;
  tags: Record<string, string>;
}

interface ErrorMetric {
  service: string;
  errorType: string;
  count: number;
  lastOccurrence: number;
}

interface PerformanceMetric {
  service: string;
  eventName: string;
  duration: number;
  success: boolean;
}

interface ServiceStatus {
  name: string;
  status: 'active' | 'inactive' | 'error';
  lastCheck: Date;
  error?: string;
}

type ConsentRates = {
  analytics: number;
  advertising: number;
  functional: number;
};

// Tipos para window
declare global {
  interface Window {
    dataLayer: DataLayerArgs[];
    Sentry?: {
      captureException: (error: Error) => void;
      captureMessage: (message: string, options: Record<string, unknown>) => void;
      addBreadcrumb: (breadcrumb: Record<string, unknown>) => void;
    };
  }
}

export class AnalyticsMonitor {
  private static instance: AnalyticsMonitor;
  private metrics: Map<string, MetricData[]> = new Map();
  private errors: ErrorMetric[] = [];
  private performance: PerformanceMetric[] = [];
  private alertThresholds: Map<string, number> = new Map();
  private alertCallbacks: Map<
    string,
    (metric: MetricData & { metricName: string; threshold: number }) => void
  > = new Map();
  private serviceStatuses: Map<string, ServiceStatus> = new Map();
  private consentRates: ConsentRates = {
    analytics: 0,
    advertising: 0,
    functional: 0,
  };
  private timers: Array<ReturnType<typeof setInterval>> = [];

  private constructor() {
    this.initializeMonitoring();
  }

  public static getInstance(): AnalyticsMonitor {
    if (!AnalyticsMonitor.instance) {
      AnalyticsMonitor.instance = new AnalyticsMonitor();
    }
    return AnalyticsMonitor.instance;
  }

  /**
   * Resets the singleton instance. For testing purposes only.
   * @internal
   */
  public static _resetInstanceForTesting(): void {
    // Detener los timers de la instancia anterior si existen
    if (AnalyticsMonitor.instance) {
      AnalyticsMonitor.instance.clearTimers();
    }
    AnalyticsMonitor.instance = null as any; // For garbage collection
  }

  private initializeMonitoring(): void {
    // Inicializar métricas base
    this.initializeMetrics();

    // Configurar thresholds por defecto
    this.setDefaultThresholds();

    // Iniciar monitoreo periódico y guardar los IDs de los timers
    this.timers.push(setInterval(() => this.checkServices(), 5 * 60 * 1000));
    this.timers.push(setInterval(() => this.updateConsentRates(), 60 * 60 * 1000));
    this.timers.push(setInterval(() => this.clearOldMetrics(), 24 * 60 * 60 * 1000));
  }

  private clearTimers(): void {
    this.timers.forEach(timerId => clearInterval(timerId));
    this.timers = [];
  }

  private initializeMetrics(): void {
    this.metrics.set('event_success_rate', []);
    this.metrics.set('script_load_time', []);
    this.metrics.set('error_rate', []);
    this.metrics.set('memory_usage', []);
    this.metrics.set('consent_rate', []);
  }

  private setDefaultThresholds(): void {
    this.alertThresholds.set('event_success_rate', 0.95); // 95% éxito
    this.alertThresholds.set('script_load_time', 2000); // 2 segundos
    this.alertThresholds.set('error_rate', 0.05); // 5% error
    this.alertThresholds.set('memory_usage', 50); // 50MB
    this.alertThresholds.set('consent_rate', 0.7); // 70% consentimiento
  }

  // Métodos de monitoreo de servicios
  public checkServices(): void {
    this.checkGTM();
    this.checkFacebookPixel();
    this.checkLinkedInInsight();
    this.checkClarity();
    this.notifyStatusChanges();
  }

  private checkGTM(): void {
    try {
      const status: ServiceStatus = {
        name: 'Google Tag Manager',
        status: 'inactive',
        lastCheck: new Date(),
      };

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        status.status = 'active';
      }

      this.serviceStatuses.set('GTM', status);
    } catch (error) {
      this.handleServiceError('GTM', error);
    }
  }

  private checkFacebookPixel(): void {
    try {
      const status: ServiceStatus = {
        name: 'Facebook Pixel',
        status: 'inactive',
        lastCheck: new Date(),
      };

      if (typeof window.fbq === 'function') {
        status.status = 'active';
      }

      this.serviceStatuses.set('FB_PIXEL', status);
    } catch (error) {
      this.handleServiceError('FB_PIXEL', error);
    }
  }

  private checkLinkedInInsight(): void {
    try {
      const status: ServiceStatus = {
        name: 'LinkedIn Insight',
        status: 'inactive',
        lastCheck: new Date(),
      };

      if (typeof window.lintrk === 'function') {
        status.status = 'active';
      }

      this.serviceStatuses.set('LINKEDIN', status);
    } catch (error) {
      this.handleServiceError('LINKEDIN', error);
    }
  }

  private checkClarity(): void {
    try {
      const status: ServiceStatus = {
        name: 'Microsoft Clarity',
        status: 'inactive',
        lastCheck: new Date(),
      };

      if (typeof window.clarity === 'function') {
        status.status = 'active';
      }

      this.serviceStatuses.set('CLARITY', status);
    } catch (error) {
      this.handleServiceError('CLARITY', error);
    }
  }

  private handleServiceError(service: string, error: unknown): void {
    const status: ServiceStatus = {
      name: service,
      status: 'error',
      lastCheck: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error',
    };

    this.serviceStatuses.set(service, status);
    this.trackError(
      new AnalyticsError(service, error instanceof Error ? error.message : 'Unknown error')
    );
  }

  private notifyStatusChanges(): void {
    const inactiveServices = Array.from(this.serviceStatuses.values()).filter(
      status => status.status !== 'active'
    );

    if (inactiveServices.length > 0) {
      analyticsLogger.warn('Analytics Monitor', 'Inactive services detected', {
        services: inactiveServices,
      });

      if (window.Sentry) {
        window.Sentry.captureMessage('Inactive Analytics Services Detected', {
          level: 'warning',
          extra: { services: inactiveServices },
        });
      }

      if (window.dataLayer) {
        window.dataLayer.push([
          'event',
          'analytics_monitoring',
          {
            event_category: 'monitoring',
            event_action: 'service_status',
            inactive_services: inactiveServices.map(s => s.name),
          },
        ] as DataLayerArgs);
      }
    }
  }

  // Métodos de monitoreo de métricas
  public trackMetric(name: string, value: number, tags: Record<string, string> = {}): void {
    const metric: MetricData = {
      timestamp: Date.now(),
      value,
      tags,
    };

    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }

    this.metrics.get(name)?.push(metric);
    this.checkThreshold(name, metric);
  }

  public trackError(error: AnalyticsError): void {
    const errorMetric: ErrorMetric = {
      service: error.service,
      errorType: error.name,
      count: 1,
      lastOccurrence: Date.now(),
    };

    const existingError = this.errors.find(
      e => e.service === error.service && e.errorType === error.name
    );

    if (existingError) {
      existingError.count++;
      existingError.lastOccurrence = Date.now();
    } else {
      this.errors.push(errorMetric);
    }

    this.trackMetric('error_rate', 1, {
      service: error.service,
      type: error.name,
    });

    if (window.Sentry) {
      window.Sentry.captureException(error);
    }

    analyticsLogger.error(error.service, error.message);
  }

  public trackPerformance(metric: PerformanceMetric): void {
    this.performance.push(metric);

    this.trackMetric('script_load_time', metric.duration, {
      service: metric.service,
      event: metric.eventName,
    });

    analyticsLogger.info(
      metric.service,
      `Performance tracked: ${metric.eventName} (${metric.duration}ms)`
    );
  }

  // Métodos de gestión de alertas
  public setAlertThreshold(metricName: string, threshold: number): void {
    this.alertThresholds.set(metricName, threshold);
  }

  public onAlert(
    metricName: string,
    callback: (metric: MetricData & { metricName: string; threshold: number }) => void
  ): void {
    this.alertCallbacks.set(metricName, callback);
  }

  private checkThreshold(metricName: string, metric: MetricData): void {
    const threshold = this.alertThresholds.get(metricName);
    const callback = this.alertCallbacks.get(metricName);

    if (threshold === undefined || !callback) {
      return;
    }

    if (metric.value > threshold) {
      analyticsLogger.warn('Analytics Monitor', `Alert threshold exceeded for ${metricName}`, {
        threshold,
        value: metric.value,
      });
      callback({ ...metric, metricName, threshold });
    }
  }

  // Métodos de consulta de datos
  public getMetrics(
    metricName: string,
    timeRange: { start: number; end: number } = {
      start: Date.now() - 24 * 60 * 60 * 1000,
      end: Date.now(),
    }
  ): MetricData[] {
    const metrics = this.metrics.get(metricName) || [];
    return metrics.filter(m => m.timestamp >= timeRange.start && m.timestamp <= timeRange.end);
  }

  public getErrors(
    timeRange: { start: number; end: number } = {
      start: Date.now() - 24 * 60 * 60 * 1000,
      end: Date.now(),
    }
  ): ErrorMetric[] {
    return this.errors.filter(
      e => e.lastOccurrence >= timeRange.start && e.lastOccurrence <= timeRange.end
    );
  }

  public getPerformanceMetrics(
    timeRange: { start: number; end: number } = {
      start: Date.now() - 24 * 60 * 60 * 1000,
      end: Date.now(),
    }
  ): PerformanceMetric[] {
    return this.performance.filter(
      p => p.duration >= timeRange.start && p.duration <= timeRange.end
    );
  }

  public getServiceStatus(service: string): ServiceStatus | undefined {
    return this.serviceStatuses.get(service);
  }

  public getConsentRates(): ConsentRates {
    return { ...this.consentRates };
  }

  // Métodos de mantenimiento
  private updateConsentRates(): void {
    const totalUsers = this.getTotalUsers();
    if (totalUsers > 0) {
      this.consentRates = {
        analytics: this.getConsentCount('analytics') / totalUsers,
        advertising: this.getConsentCount('advertising') / totalUsers,
        functional: this.getConsentCount('functional') / totalUsers,
      };

      this.trackMetric(
        'consent_rate',
        (this.consentRates.analytics +
          this.consentRates.advertising +
          this.consentRates.functional) /
          3
      );

      if (window.dataLayer) {
        window.dataLayer.push([
          'event',
          'consent_monitoring',
          {
            event_category: 'monitoring',
            event_action: 'consent_rates',
            analytics_rate: this.consentRates.analytics,
            advertising_rate: this.consentRates.advertising,
            functional_rate: this.consentRates.functional,
          },
        ] as DataLayerArgs);
      }
    }
  }

  private getTotalUsers(): number {
    return 100; // Implementar lógica real de conteo de usuarios
  }

  private getConsentCount(_type: string): number {
    return 50; // Implementar lógica real de conteo de consentimiento
  }

  public clearOldMetrics(retentionDays: number = 30): void {
    const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;

    this.metrics.forEach((metrics, name) => {
      this.metrics.set(
        name,
        metrics.filter(m => m.timestamp >= cutoff)
      );
    });

    this.errors = this.errors.filter(e => e.lastOccurrence >= cutoff);
    this.performance = this.performance.filter(p => p.duration >= cutoff);

    analyticsLogger.info(
      'Analytics Monitor',
      `Old metrics cleared (retention: ${retentionDays} days)`
    );
  }
}

// Exportar instancia singleton
export const analyticsMonitor = AnalyticsMonitor.getInstance();
export default analyticsMonitor;
